/**
 * Dashboard — the Mini App's "menu" surface (reached from the chat
 * header's grid icon).
 *
 * Design intent (2026-06 polish): keep it scannable, not a wall of text.
 * Three KPI tiles up top, then the workspace → project tree as the
 * primary navigation (projects open as their own pages), then a compact
 * "needs attention" strip (top action items + risks). Empty states are a
 * single slim line, not paragraphs. All management affordances are
 * icon-buttons that clearly read as buttons.
 *
 * Split into a presentational `DashboardView` (rendered with mock data by
 * the dev preview harness) and a `Dashboard` container that fetches.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { fetchDashboard } from "../api/dashboard";
import { fetchMe, setActiveProject } from "../api/users";
import { deleteWorkspace, renameWorkspace } from "../api/workspaces";
import { IconButton, IconLink } from "../components/IconButton";
import { LoadingScreen } from "../components/LoadingScreen";
import { RelativeTime } from "../components/RelativeTime";
import {
  AlertIcon,
  CheckIcon,
  ChecklistIcon,
  ChevronRightIcon,
  FolderIcon,
  InboxIcon,
  PencilIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
  XIcon,
} from "../components/icons";
import { useBackButton } from "../hooks/useBackButton";
import { haptic } from "../hooks/useHaptic";
import type {
  DashboardActionItem,
  DashboardRecentRisk,
  DashboardResponse,
  DashboardWorkspace,
  RiskSeverity,
  TaskPriority,
} from "../lib/types";

const HIGHLIGHT_LIMIT = 3;

const PRIORITY_TINT: Record<TaskPriority, string> = {
  low: "var(--color-hint)",
  medium: "var(--color-link)",
  high: "var(--color-destructive)",
};

const SEVERITY_TINT: Record<RiskSeverity, string> = {
  low: "var(--color-hint)",
  medium: "var(--color-link)",
  high: "var(--color-destructive)",
};

/* ====================================================================== */
/* Container                                                              */
/* ====================================================================== */

export function Dashboard(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useBackButton(useCallback(() => navigate("/"), [navigate]));

  const { data, isLoading, isError, error, refetch } =
    useQuery<DashboardResponse>({
      queryKey: ["dashboard"],
      queryFn: ({ signal }) => fetchDashboard(signal),
    });

  // Current user → which project is pinned as the capture target.
  const me = useQuery({ queryKey: ["me"], queryFn: ({ signal }) => fetchMe(signal) });
  const activeProjectId = me.data?.active_project_id ?? null;
  const [pendingActiveId, setPendingActiveId] = useState<string | null>(null);

  const activeMutation = useMutation({
    // Tapping the pinned project again clears it (toggle), matching the bot.
    mutationFn: (projectId: string) =>
      setActiveProject(projectId === activeProjectId ? null : projectId),
    onMutate: (projectId: string) => setPendingActiveId(projectId),
    onSuccess: async () => {
      haptic.notify("success");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: () => haptic.notify("error"),
    onSettled: () => setPendingActiveId(null),
  });

  if (isLoading) return <LoadingScreen message="Loading dashboard…" />;
  if (isError || !data) {
    return (
      <Page>
        <Header />
        <CenteredError
          title="Couldn't load the dashboard"
          message={error instanceof Error ? error.message : "Unknown error"}
          onRetry={() => void refetch()}
        />
      </Page>
    );
  }

  return (
    <DashboardView
      data={data}
      activeProjectId={activeProjectId}
      settingActiveId={pendingActiveId}
      onSetActive={(projectId) => activeMutation.mutate(projectId)}
    />
  );
}

/* ====================================================================== */
/* Presentational                                                        */
/* ====================================================================== */

/** Active-project picker wiring threaded from the container down to each
 *  project row. Kept as props so `DashboardView` stays presentational
 *  (the dev preview supplies its own mock handlers). */
interface ActiveControls {
  activeProjectId: string | null;
  settingActiveId: string | null;
  onSetActive: (projectId: string) => void;
}

export function DashboardView({
  data,
  activeProjectId = null,
  settingActiveId = null,
  onSetActive,
}: {
  data: DashboardResponse;
} & Partial<ActiveControls>): React.ReactElement {
  const actions = data.today_action_items.slice(0, HIGHLIGHT_LIMIT);
  const risks = data.recent_risks.slice(0, HIGHLIGHT_LIMIT);
  const active: ActiveControls = {
    activeProjectId,
    settingActiveId,
    onSetActive: onSetActive ?? (() => undefined),
  };

  return (
    <Page>
      <Header />

      <main className="px-3 pt-3 pb-10 space-y-5">
        <StatsRow
          pending={data.pending_actions}
          overdue={data.overdue}
          processed={data.processed_messages}
        />

        {/* Primary navigation — workspaces & projects. */}
        <section>
          <SectionHeader
            icon={<FolderIcon size={15} />}
            title="Workspaces"
            action={
              <IconLink
                to="/workspaces/new"
                label="New workspace"
                variant="primary"
              >
                <PlusIcon size={18} />
              </IconLink>
            }
          />
          {data.workspaces.length === 0 ? (
            <EmptyWorkspaces />
          ) : (
            <ul className="space-y-2.5">
              {data.workspaces.map((ws) => (
                <WorkspaceCard key={ws.id} ws={ws} active={active} />
              ))}
            </ul>
          )}
        </section>

        {/* Needs attention — compact, capped. */}
        <section>
          <SectionHeader
            icon={<ChecklistIcon size={15} />}
            title="Action items"
            count={data.pending_actions}
          />
          {actions.length === 0 ? (
            <SlimEmpty icon={<InboxIcon size={16} />} text="Nothing pending" />
          ) : (
            <ul className="space-y-1.5">
              {actions.map((task) => (
                <li key={task.id}>
                  <ActionRow task={task} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <SectionHeader
            icon={<AlertIcon size={15} />}
            title="Risks"
            count={risks.length}
          />
          {risks.length === 0 ? (
            <SlimEmpty icon={<CheckIcon size={16} />} text="No risks flagged" />
          ) : (
            <ul className="space-y-1.5">
              {risks.map((risk) => (
                <li key={risk.id}>
                  <RiskRow risk={risk} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </Page>
  );
}

/* ---------- Page chrome ----------------------------------------------- */

function Page({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div
      className="min-h-dvh"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      {children}
    </div>
  );
}

function Header(): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-10 px-4 py-3"
      style={{
        background: "var(--color-bg)",
        borderBottom: "1px solid var(--surface-hairline)",
      }}
    >
      <h1 className="text-[17px] font-semibold">Dashboard</h1>
    </header>
  );
}

function SectionHeader({
  icon,
  title,
  count,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  count?: number;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 mb-2 px-0.5 min-h-[34px]">
      {icon ? (
        <span style={{ color: "var(--color-hint)" }}>{icon}</span>
      ) : null}
      <h2
        className="text-[12px] font-semibold uppercase tracking-wider"
        style={{ color: "var(--color-hint)" }}
      >
        {title}
      </h2>
      {typeof count === "number" && count > 0 ? (
        <span
          className="text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full leading-none"
          style={{
            background: "var(--color-secondary-bg)",
            color: "var(--color-text)",
          }}
        >
          {count}
        </span>
      ) : null}
      <span className="flex-1" />
      {action}
    </div>
  );
}

/* ---------- Workspace card -------------------------------------------- */

function WorkspaceCard({
  ws,
  active,
}: {
  ws: DashboardWorkspace;
  active: ActiveControls;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(ws.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isOwner = ws.role === "owner";

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });

  const renameMutation = useMutation({
    mutationFn: (next: string) => renameWorkspace(ws.id, next),
    onSuccess: async () => {
      haptic.notify("success");
      setRenaming(false);
      await invalidate();
    },
    onError: () => haptic.notify("error"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteWorkspace(ws.id),
    onSuccess: async () => {
      haptic.notify("success");
      await invalidate();
    },
    onError: () => haptic.notify("error"),
  });

  return (
    <li
      className="rounded-[16px] p-3"
      style={{
        background: "var(--color-secondary-bg)",
        boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
      }}
    >
      {/* Title row */}
      <div className="flex items-center gap-2">
        {renaming ? (
          <>
            <input
              type="text"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              disabled={renameMutation.isPending}
              className="flex-1 min-w-0 rounded-[10px] px-3 py-1.5 text-[15px] outline-none"
              style={{
                background: "var(--color-bg)",
                color: "var(--color-text)",
                boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
              }}
            />
            <IconButton
              label="Save name"
              variant="primary"
              disabled={
                renameMutation.isPending ||
                name.trim().length === 0 ||
                name.trim() === ws.name
              }
              onClick={() => renameMutation.mutate(name.trim())}
            >
              <CheckIcon size={18} />
            </IconButton>
            <IconButton
              label="Cancel rename"
              onClick={() => {
                setName(ws.name);
                setRenaming(false);
              }}
            >
              <XIcon size={18} />
            </IconButton>
          </>
        ) : (
          <>
            <h3 className="flex-1 min-w-0 truncate text-[15px] font-semibold">
              {ws.name}
            </h3>
            <IconLink
              to={`/projects/new?workspace=${encodeURIComponent(ws.id)}`}
              label="New project"
              variant="primary"
            >
              <PlusIcon size={18} />
            </IconLink>
            <IconButton label="Rename workspace" onClick={() => setRenaming(true)}>
              <PencilIcon size={16} />
            </IconButton>
            {isOwner ? (
              <IconButton
                label="Delete workspace"
                variant="danger"
                onClick={() => setConfirmDelete(true)}
              >
                <TrashIcon size={16} />
              </IconButton>
            ) : null}
          </>
        )}
      </div>

      {renameMutation.isError ? (
        <ErrLine>{describeApiError(renameMutation.error)}</ErrLine>
      ) : null}

      {confirmDelete ? (
        <div
          className="mt-2.5 rounded-[12px] p-3 space-y-2.5"
          style={{
            background:
              "color-mix(in srgb, var(--color-destructive) 10%, transparent)",
            boxShadow:
              "inset 0 0 0 1px color-mix(in srgb, var(--color-destructive) 28%, transparent)",
          }}
        >
          <p className="text-[12.5px] leading-snug">
            Delete <strong>{ws.name}</strong>
            {ws.projects.length > 0
              ? ` and its ${ws.projects.length} project${ws.projects.length === 1 ? "" : "s"}`
              : ""}
            ? Notes stay in memory. Can't be undone.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              className="flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 rounded-full disabled:opacity-40 active:scale-95 transition-transform"
              style={{
                background: "var(--color-destructive)",
                color: "var(--color-button-text)",
              }}
            >
              <TrashIcon size={15} />
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </button>
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmDelete(false)}
              className="text-[13px] px-3 py-1.5 rounded-full active:scale-95 transition-transform"
              style={{
                background: "var(--color-bg)",
                color: "var(--color-text)",
                boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
              }}
            >
              Cancel
            </button>
          </div>
          {deleteMutation.isError ? (
            <ErrLine>{describeApiError(deleteMutation.error)}</ErrLine>
          ) : null}
        </div>
      ) : null}

      {/* Projects */}
      {ws.projects.length === 0 ? (
        <p
          className="mt-2 px-1 text-[12px]"
          style={{ color: "var(--color-hint)" }}
        >
          No projects yet — tap{" "}
          <span style={{ color: "var(--color-link)" }}>＋</span> to add one.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5">
          {ws.projects.map((p) => (
            <li key={p.id}>
              <ProjectRow
                id={p.id}
                name={p.name}
                isActive={active.activeProjectId === p.id}
                isBusy={active.settingActiveId === p.id}
                onToggleActive={() => active.onSetActive(p.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** A project row: tap the body to open it, tap the ★ to pin/unpin it as the
 *  active capture target. The pinned project gets an accent ring + "Active"
 *  badge so the choice is obvious. The star is a sibling button (not nested
 *  in the navigating link) to keep the markup valid + taps unambiguous. */
function ProjectRow({
  id,
  name,
  isActive,
  isBusy,
  onToggleActive,
}: {
  id: string;
  name: string;
  isActive: boolean;
  isBusy: boolean;
  onToggleActive: () => void;
}): React.ReactElement {
  return (
    <div
      className="flex items-center gap-1 rounded-[11px] pr-1"
      style={{
        background: isActive
          ? "color-mix(in srgb, var(--color-button) 14%, var(--color-bg))"
          : "var(--color-bg)",
        boxShadow: isActive
          ? "inset 0 0 0 1.5px var(--color-button)"
          : "inset 0 0 0 1px var(--surface-hairline)",
      }}
    >
      <Link
        to={`/projects/${encodeURIComponent(id)}`}
        className="flex items-center gap-2 flex-1 min-w-0 text-[14px] py-2.5 pl-2.5 active:opacity-70 transition-opacity"
        style={{ color: "var(--color-text)" }}
      >
        <FolderIcon size={16} />
        <span className="min-w-0 truncate">{name}</span>
        {isActive ? (
          <span
            className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full leading-none shrink-0"
            style={{
              background: "var(--color-button)",
              color: "var(--color-button-text)",
            }}
          >
            Active
          </span>
        ) : null}
        <span className="flex-1" />
        <span style={{ color: "var(--color-hint)" }}>
          <ChevronRightIcon size={16} />
        </span>
      </Link>
      <IconButton
        label={isActive ? "Clear active project" : "Set as active project"}
        variant={isActive ? "primary" : "default"}
        size={36}
        disabled={isBusy}
        onClick={onToggleActive}
      >
        <StarIcon size={17} filled={isActive} />
      </IconButton>
    </div>
  );
}

function EmptyWorkspaces(): React.ReactElement {
  return (
    <Link
      to="/workspaces/new"
      className="flex items-center gap-3 rounded-[16px] p-4 active:scale-[0.99] transition-transform"
      style={{
        background: "var(--color-secondary-bg)",
        boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
      }}
    >
      <span
        className="grid place-items-center rounded-full shrink-0"
        style={{
          width: 38,
          height: 38,
          background: "var(--color-button)",
          color: "var(--color-button-text)",
        }}
      >
        <PlusIcon size={20} />
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold">
          Create a workspace
        </span>
        <span className="block text-[12px]" style={{ color: "var(--color-hint)" }}>
          Group your projects for a team or client.
        </span>
      </span>
    </Link>
  );
}

/* ---------- Stats row ------------------------------------------------- */

function StatsRow({
  pending,
  overdue,
  processed,
}: {
  pending: number;
  overdue: number;
  processed: number;
}): React.ReactElement {
  return (
    <div className="grid grid-cols-3 gap-2">
      <StatTile value={pending} label="Pending" />
      <StatTile value={overdue} label="Overdue" emphasise={overdue > 0} />
      <StatTile value={processed} label="Processed" />
    </div>
  );
}

function StatTile({
  value,
  label,
  emphasise,
}: {
  value: number;
  label: string;
  emphasise?: boolean;
}): React.ReactElement {
  return (
    <div
      className="rounded-[16px] py-3.5 px-2 text-center"
      style={{
        background: emphasise
          ? "color-mix(in srgb, var(--color-destructive) 14%, transparent)"
          : "var(--color-secondary-bg)",
        boxShadow: emphasise
          ? "inset 0 0 0 1px color-mix(in srgb, var(--color-destructive) 30%, transparent)"
          : "inset 0 0 0 1px var(--surface-hairline)",
      }}
    >
      <div
        className="text-[24px] font-bold leading-none tabular-nums"
        style={emphasise ? { color: "var(--color-destructive)" } : undefined}
      >
        {value}
      </div>
      <div
        className="text-[10px] font-medium uppercase tracking-wider mt-1.5"
        style={{ color: "var(--color-hint)" }}
      >
        {label}
      </div>
    </div>
  );
}

/* ---------- Highlight rows -------------------------------------------- */

function SlimEmpty({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}): React.ReactElement {
  return (
    <div
      className="flex items-center justify-center gap-2 rounded-[12px] py-2.5 text-[12.5px]"
      style={{ background: "var(--color-secondary-bg)", color: "var(--color-hint)" }}
    >
      {icon}
      {text}
    </div>
  );
}

function ActionRow({ task }: { task: DashboardActionItem }): React.ReactElement {
  const inner = (
    <div
      className="rounded-[12px] p-2.5 flex items-center gap-2.5"
      style={{
        background: "var(--color-secondary-bg)",
        boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
      }}
    >
      <span
        className="w-[3px] self-stretch rounded-full shrink-0"
        style={{
          background: task.priority
            ? PRIORITY_TINT[task.priority]
            : "var(--color-hint)",
        }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium leading-snug truncate">
          {task.title}
        </div>
        <div
          className="text-[11px] mt-0.5 flex flex-wrap gap-x-2"
          style={{ color: "var(--color-hint)" }}
        >
          {task.assignee ? <span className="truncate">{task.assignee}</span> : null}
          {task.deadline ? <span>{task.deadline}</span> : null}
          <RelativeTime iso={task.created_at} />
        </div>
      </div>
      {task.project_id ? (
        <span style={{ color: "var(--color-hint)" }}>
          <ChevronRightIcon size={16} />
        </span>
      ) : null}
    </div>
  );
  return task.project_id ? (
    <Link
      to={`/projects/${encodeURIComponent(task.project_id)}?tab=tasks`}
      className="block active:scale-[0.99] transition-transform"
      style={{ color: "var(--color-text)" }}
    >
      {inner}
    </Link>
  ) : (
    inner
  );
}

function RiskRow({ risk }: { risk: DashboardRecentRisk }): React.ReactElement {
  const tint = SEVERITY_TINT[risk.severity];
  const inner = (
    <div
      className="rounded-[12px] p-2.5 flex items-center gap-2.5"
      style={{
        background: "var(--color-secondary-bg)",
        boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
      }}
    >
      <span
        className="grid place-items-center rounded-full shrink-0"
        style={{
          width: 26,
          height: 26,
          background: `color-mix(in srgb, ${tint} 18%, transparent)`,
          color: tint,
        }}
      >
        <AlertIcon size={15} />
      </span>
      <p className="flex-1 min-w-0 text-[13px] leading-snug line-clamp-2">
        {risk.content}
      </p>
      {risk.project_id ? (
        <span style={{ color: "var(--color-hint)" }}>
          <ChevronRightIcon size={16} />
        </span>
      ) : null}
    </div>
  );
  return risk.project_id ? (
    <Link
      to={`/projects/${encodeURIComponent(risk.project_id)}?tab=risks`}
      className="block active:scale-[0.99] transition-transform"
      style={{ color: "var(--color-text)" }}
    >
      {inner}
    </Link>
  ) : (
    inner
  );
}

/* ---------- Tiny primitives ------------------------------------------- */

function ErrLine({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <p
      className="text-[11.5px] mt-2"
      style={{ color: "var(--color-destructive)" }}
      role="alert"
    >
      {children}
    </p>
  );
}

function describeApiError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unknown error";
}

function CenteredError({
  title,
  message,
  onRetry,
}: {
  title: string;
  message?: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
      <h2 className="text-[16px] font-semibold">{title}</h2>
      {message ? (
        <p className="text-[13px]" style={{ color: "var(--color-hint)" }}>
          {message}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onRetry}
        className="rounded-full px-4 py-2 text-[13px] font-medium active:scale-95 transition-transform"
        style={{
          background: "var(--color-button)",
          color: "var(--color-button-text)",
        }}
      >
        Retry
      </button>
    </div>
  );
}
